import axiosInstance from '../helpers/axiosInstance'

const PLATFORM_URL = process.env.REACT_APP_PLATFORM_URL

/**
 * GET /api/v1/shipping-methods/:id/tarif-zones-status?zone=&only_gaps=
 *
 * Returns: {
 *   is_cubbo,                        // top-level, per method
 *   zones: [{
 *     zone,
 *     tiers: [{ shipping_cost_id, cost, max_compound_weight, currency_code, coverage_enabled, selectable }],
 *     has_cost,                      // true when ≥1 tier is runtime-selectable
 *     has_coverage,
 *     is_cubbo,
 *     has_xdock_zone,
 *     missing: [],                   // runtime-breaking gaps: MISSING_COST / MISSING_XDOCK_ZONE / MISSING_XDOCK_ROUTE / …
 *     notices: [],                   // informational: UNUSED_COST / COST_UNREACHABLE / …
 *   }],
 * }
 * (cost / shipping_cost_id live INSIDE tiers[], not at the zone level.)
 */
export const fetchTarifZonesStatus = async (shippingMethodId, { zone, only_gaps } = {}) => {
  const params = {}
  if (zone) params.zone = zone
  if (only_gaps) params.only_gaps = true
  const res = await axiosInstance.get(`${PLATFORM_URL}/api/v1/shipping-methods/${shippingMethodId}/tarif-zones-status`, { params })
  return res.data
}

/**
 * POST /api/v1/shipping-costs/bulk-update
 *
 * payload: { items: [{ id, cost }], dry_run }
 *
 * Returns the full body — 200 { valid, row_errors } on dry-run, 202 { job_id } on
 * commit, 422 { valid: false, row_errors } on validation failure. The caller
 * decides what to render from response.job_id / response.row_errors.
 */
export const bulkUpdateShippingCosts = async payload => {
  try {
    const res = await axiosInstance.post(`${PLATFORM_URL}/api/v1/shipping-costs/bulk-update`, payload)
    return res.data
  } catch (err) {
    if (err.response && err.response.status === 422) {
      return err.response.data
    }
    throw err
  }
}

/**
 * POST /api/v1/shipping-costs/bulk-create
 *
 * payload: {
 *   shipping_method_id,
 *   items: [{ tarif_zone, cost, max_compound_weight, currency_code? }],
 *   xdock_bindings: [{ zone, xdock_id }],   // required for Cubbo zones
 *   dry_run
 * }
 *
 * Same response contract as bulkUpdateShippingCosts.
 */
export const bulkCreateShippingCosts = async payload => {
  try {
    const res = await axiosInstance.post(`${PLATFORM_URL}/api/v1/shipping-costs/bulk-create`, payload)
    return res.data
  } catch (err) {
    if (err.response && err.response.status === 422) {
      return err.response.data
    }
    throw err
  }
}

/**
 * GET /api/v1/shipping-costs/update-status/:jid
 *
 * Returns: { status, progress_percentage, task_type, result } — same shape as the
 * coverages status endpoint, so it plugs straight into useCoverageJobStatus.
 */
export const fetchShippingCostsUpdateStatus = async jid => {
  const res = await axiosInstance.get(`${PLATFORM_URL}/api/v1/shipping-costs/update-status/${jid}`)
  return res.data
}

/**
 * PUT /api/v1/shipping-costs/zone — replace a zone's weight tiers (upsert + delete)
 * and, for Cubbo, (re)assign its cross-dock, in one synchronous request.
 * payload: { shipping_method_id, tarif_zone, currency_code?, xdock_id?,
 *            tiers: [{ id?, max_compound_weight, cost }] }
 */
export const syncZoneCosts = async payload => {
  const res = await axiosInstance.put(`${PLATFORM_URL}/api/v1/shipping-costs/zone`, payload)
  return res.data
}
